/* 契約書署名 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Block from '../../molecules/Block';
import Heading from '../../molecules/Heading';
import StepNavigation from '../../molecules/StepNavigation';
import TransitionButton from '../../molecules/TransitionButton';
import { handleBrowserBack } from '../../BrowserBack';
import Annotation from '../../atoms/Annotation';
import Text from '../../molecules/Text';
import CenterButton from '../../atoms/CenterButton';
import axios from '../../axios';
import Popup from '../../molecules/Popup';
import LoadingModal from '../../molecules/LoadingModal';
import { Button } from 'react-bootstrap';
import { api_response_type, pin_code_send_type, status_code_list } from '../../Const';
import { KDANKGM05020A01, KDANKGM05020A02 } from '../../const/api_modules';

interface Signature {
  signature_id: string;
  signature_key: string;
}

const ContractSignature: React.FC = () => {
  const [signature, setSignature] = useState<Signature>({ signature_id: '', signature_key: '' });
  // エラーメッセージ
  const [error_message, setErrorMessage] = useState({
    'receptions.signature_id': '',
    'receptions.signature_key': '',
  });
  const [modal_show, setModalShow] = useState(false);
  const [popup_button, setPopupButton] = useState({
    label: '閉じる',
    variant: 'outline-success',
    handleClick: () => setModalShow(false),
  });
  const [is_loading, setIsLoading] = useState(false);
  // データ属性から情報を取得する
  const node = document.getElementById('contract-signature');
  const r_id = node?.dataset.r_id;
  const r_cd = node?.dataset.r_cd;
  const pin_code_type = node?.dataset.pin_code_type;

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
  }, []);

  // 「ID・KEY再送」ボタン押下時
  const handleResendClick = async () => {
    try {
      setIsLoading(true);

      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/common`,
        {
          reception_cd: r_cd,
          reception_id: r_id,
          module_name: KDANKGM05020A01,
        }
      );

      if (response.data.response_type === api_response_type.signature_resend_complete) {
        // 正常終了の場合
        setModalShow(true);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  // 文字入力時の処理
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSignature({ ...signature, [event.target.id]: event.target.value });
  };

  // 「署名する」押下時
  const handleClick = async () => {
    // エラーメッセージの初期化
    setErrorMessage({ 'receptions.signature_id': '', 'receptions.signature_key': '' });

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/common`,
        {
          reception_cd: r_cd,
          reception_id: r_id,
          module_name: KDANKGM05020A02,
          detail: [
            { id: 7670, name: 'receptions.signature_id', value: signature.signature_id },
            { id: 7680, name: 'receptions.signature_key', value: signature.signature_key },
          ],
        }
      );

      if (response.data.response_type === api_response_type.navigation_finish) {
        // 正常終了の場合
        // 契約署名完了画面に遷移
        location.href = `${location.origin}${process.env.MIX_MOCK_URL}/applicant/contract_signature_result?r_id=${r_id}`;
      } else {
        // 異常終了の場合

        // 署名エラーの場合のエラー表示
        if (response.data.message) {
          alert(response.data.message);
        }

        // バリデーションエラーの場合のエラー表示
        setErrorMessage(prev => {
          response.data.detail.map((value: any) => {
            prev = { ...prev, [value.form_name]: value.message };
          });
          return prev;
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      alert(error.response.data.message);
      setIsLoading(false);
    }
  };

  // 戻るボタン押下時の処理
  const backTransition = () => {
    location.replace(
      // 契約書確認画面へ遷移する。(契約書署名画面への遷移時に更新されたステータスを考慮してURLに設定する。)
      `${location.origin}${process.env.MIX_MOCK_URL}/reception/${r_cd}/${r_id}/${status_code_list.contract_conclusion}`
    );
  };

  return (
    <>
      <Heading label="契約手続き" size="l" />
      <StepNavigation
        current={2}
        step1="電子契約書確認"
        step2="電子署名ID・KEY\n入力"
        step3="電子署名完了"
        disabled={false}
      />
      <Block
        label="PINコード(電子署名ID・KEY)入力"
        content={
          <>
            <div className="p-lead my-3">
              {pin_code_type === pin_code_send_type.mail ? (
                // メール
                <>
                  PINコード(電子署名ID・KEY)をメールで送信しました。
                  <br />
                  メールに記載のID・KEYを、下記に入力して下さい。
                  <br />
                  ※この画面は閉じずにご確認下さい。
                  <br />
                  ※お使いのメールシステムによっては、ID・KEYのお知らせが届くまで時間がかかることがあります。数分待ってもID・KEYが届かない場合は、メールの設定状況を確認下さい。
                  <br />
                  その後、ID・KEYの再送ボタンを押して下さい。
                  <br />
                  <Button variant="contract_resend" onClick={handleResendClick}>
                    ID・KEY再送
                  </Button>
                </>
              ) : pin_code_type === pin_code_send_type.sms ? (
                // sms
                <>
                  PINコード(電子署名ID・KEY)をSMSで送信しました。
                  <br />
                  SMSに記載のID・KEYを、下記に入力して下さい。
                  <br />
                  ※この画面は閉じずにご確認下さい。
                  <br />
                  ※環境によっては、ID・KEYのお知らせが届くまで時間がかかることがあります。数分待ってもID・KEYが届かない場合は、SMSの設定状況を確認下さい。
                  <br />
                  その後、ID・KEYの再送ボタンを押して下さい。
                  <br />
                  <Button variant="contract_resend" onClick={handleResendClick}>
                    ID・KEY再送
                  </Button>
                </>
              ) : (
                // 本人限定郵便
                <>
                  契約に用いるPINコード(電子署名ID・KEY)を本人限定郵便で送付しました。
                  <br />
                  本人限定郵便をお受け取り後、マイページから契約手続きを再開し、本人限定郵便に記載のID・KEYを下記に入力して下さい。
                  <br />
                  ※本人限定郵便は到着まで数日かかります。届かない場合は申込みJAにお問合せ下さい。連絡先の電話番号はJAバンクホームページからご確認下さい。
                  <br />
                </>
              )}
            </div>
            <Annotation text="電子署名ID・KEYを入力して下さい。" />
            <div className="mb-5">
              <Text
                id="signature_id"
                label="電子署名ID"
                value={signature.signature_id}
                placeholder=""
                handleChange={handleChange}
                message={error_message['receptions.signature_id']}
                disabled={false}
              />
              <Text
                id="signature_key"
                label="KEY"
                value={signature.signature_key}
                placeholder=""
                handleChange={handleChange}
                message={error_message['receptions.signature_key']}
                disabled={false}
              />
            </div>
            <CenterButton label="署名する" handleClick={handleClick} btn_class="arrow-right" />
            <TransitionButton label="戻る" handleClick={backTransition} />
          </>
        }
      />
      <Popup
        title="電子署名ID・KEYの再送"
        text="電子署名ID・KEYを再送しました。"
        toggleShow={modal_show}
        toggleClose={() => setModalShow(false)}
        first_button={popup_button}
      />
      <LoadingModal show={is_loading} />
    </>
  );
};

if (document.getElementById('contract-signature')) {
  ReactDOM.render(<ContractSignature />, document.getElementById('contract-signature'));
}
